import type { ReactNode } from 'react';
import { AdminContextMenu } from '@rocco/ui/tile-shared/components/AdminContextMenu';
import type { ArticleTileViewModel } from '@rocco/ui/tile-article/view-models/article';
import { useArticleHeroPin } from './ArticleHeroPinContext';
import { useTileAdminActions } from '../hooks/use-tile-admin-actions';

interface ArticleTileAdminMenuProps {
    article: ArticleTileViewModel;
    children: ReactNode;
}

/**
 * Inner menu, mounted only once the toggle exists, so the admin-action hook
 * and its fetcher never run for an ordinary reader.
 */
const AdminMenu = ({
    article,
    toggle,
    pinned,
    children,
}: ArticleTileAdminMenuProps & {
    toggle: (articleID: number, pin: boolean) => void;
    pinned: boolean;
}) => {
    const actions = useTileAdminActions({
        item: {
            id: String(article.id),
            link: article.link,
            professionalLink: article.professional.link ?? '',
        },
        itemType: 'articles',
    });

    // Named rather than spread. `useTileAdminActions` also returns Reposition
    // Image, Delete and Pin To Homepage, and none of the three belongs here:
    // repositioning does not work on an article, deleting from a listing
    // cannot be undone, and the homepage pin reads its Pin/Unpin wording off
    // admin data the editorial grid does not fetch, so it would always say
    // "Pin".
    return (
        <AdminContextMenu
            onEdit={actions.onEdit}
            onToggleHeroPin={() => toggle(article.id, !pinned)}
            // No `created`: the timestamp footer other tiles carry needs the
            // batched admin fetch, which the editorial grids do not make.
            statusInfo={{ heroPinned: pinned }}
        >
            {children}
        </AdminContextMenu>
    );
};

/**
 * Wraps an article tile with the staff right-click menu: edit, reposition the
 * image, and pin the article to the article directory's hero carousel.
 *
 * Readers and non-admin members get the tile unchanged — with no items the
 * Radix trigger is disabled, so right-click stays the browser's own.
 */
export const ArticleTileAdminMenu = ({
    article,
    children,
}: ArticleTileAdminMenuProps) => {
    const { isPinned, toggle } = useArticleHeroPin();

    if (!toggle) return <>{children}</>;

    return (
        <AdminMenu
            article={article}
            toggle={toggle}
            pinned={isPinned(article.id)}
        >
            {children}
        </AdminMenu>
    );
};
